import { useState, useEffect, createContext, useContext, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { installFingerprintInterceptor } from "./utils/deviceFingerprint";
import { lazyWithRetry, installGlobalChunkErrorReload, clearChunkReloadFlag } from "./utils/lazyWithRetry";

// Auto-recover from stale lazy-chunk failures (after a redeploy) BEFORE the
// React tree even mounts. Belt-and-suspenders with ChunkErrorBoundary.
installGlobalChunkErrorReload();

// Auto-attach device fingerprint to every API call for referral-fraud detection
installFingerprintInterceptor(axios);
import { Toaster } from "react-hot-toast";
import { Toaster as SonnerToaster } from "sonner";

// Components
import AgeVerification from "./components/AgeVerification";
import StickyBottomNav from "./components/StickyBottomNav";
import FloatingActionButton from "./components/FloatingActionButton";
import ComplianceFilter from "./components/ComplianceFilter";
// LevelUpCelebration import removed (Feb 2026 emergency) — popup killed to stop the production loop.
import LocationVerifyModal from "./components/LocationVerifyModal";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import RedeemErrorBoundary from "./components/RedeemErrorBoundary";

// Contexts
import { GeoProvider, useGeo } from "./contexts/GeoContext";
import SCBlockedBanner from "./components/SCBlockedBanner";

// ─── EAGER (above-the-fold / common) ─────────────────────────────────────
import LandingPage from "./pages/LandingPage";
import GameLobby from "./pages/GameLobby";
// Non-critical pages are code-split via lazyWithRetry below — massively
// reduces initial bundle size and makes section-to-section navigation feel
// instant on prod. Keep only entry points (LandingPage + GameLobby) eager.
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Store = lazyWithRetry(() => import("./pages/Store"));
// Redeem flow is eager-loaded — these pages must NEVER black-screen on a
// chunk-load failure (cash-out is the most stress-sensitive flow we have).
const RedeemPage = lazyWithRetry(() => import("./pages/RedeemPage"));
const RedemptionStatusPage = lazyWithRetry(() => import("./pages/RedemptionStatusPage"));

// ─── LAZY (heavy / rarely visited routes) ────────────────────────────────
// Each lazy() becomes its own chunk → not downloaded until the user navigates there.
// Massive win for mobile: 10K-line AdminDashboard is no longer in the initial bundle.
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const ModeratorDashboard = lazyWithRetry(() => import("./pages/ModeratorDashboard"));
const SeniorModDashboard = lazyWithRetry(() => import("./pages/SeniorModDashboard"));
const AdminGameControls = lazyWithRetry(() => import("./pages/AdminGameControls"));
const PremiumSlotGame = lazyWithRetry(() => import("./pages/PremiumSlotGame"));
const MultiLayoutSlot = lazyWithRetry(() => import("./pages/MultiLayoutSlot"));
const SlotopolGame = lazyWithRetry(() => import("./pages/SlotopolGame"));
const ExternalSlotGame = lazyWithRetry(() => import("./pages/ExternalSlotGame"));
const BoomingGamePlayer = lazyWithRetry(() => import("./pages/BoomingGamePlayer"));
const BetsoftGamePlayer = lazyWithRetry(() => import("./pages/BetsoftGamePlayer"));
const MascotGamePlayer = lazyWithRetry(() => import("./pages/MascotGamePlayer"));
const KAGamePlayer = lazyWithRetry(() => import("./pages/KAGamePlayer"));
const SpinjoyGamePlayer = lazyWithRetry(() => import("./pages/SpinjoyGamePlayer"));
const PetersonsGamePlayer = lazyWithRetry(() => import("./pages/PetersonsGamePlayer"));
const Blackjack = lazyWithRetry(() => import("./pages/Blackjack"));
const CrashGame = lazyWithRetry(() => import("./pages/CrashGame"));
const RouletteGame = lazyWithRetry(() => import("./pages/RouletteGame"));
const PokerGame = lazyWithRetry(() => import("./pages/PokerGame"));
const DailyWheel = lazyWithRetry(() => import("./pages/DailyWheel"));
const GiveawayWheel = lazyWithRetry(() => import("./pages/GiveawayWheel"));
const TournamentsPage = lazyWithRetry(() => import("./pages/TournamentsPage"));
const Leaderboard = lazyWithRetry(() => import("./pages/Leaderboard"));
const JackpotPage = lazyWithRetry(() => import("./pages/JackpotPage"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const VerificationPage = lazyWithRetry(() => import("./pages/VerificationPage"));
const W9FormPage = lazyWithRetry(() => import("./pages/W9FormPage"));
const TaxDocumentsPage = lazyWithRetry(() => import("./pages/TaxDocumentsPage"));
const ReferralProgram = lazyWithRetry(() => import("./pages/ReferralProgram"));
const MyReferralsPage = lazyWithRetry(() => import("./pages/MyReferralsPage"));
const WhyCrypto = lazyWithRetry(() => import("./pages/WhyCrypto"));
const ForcePasswordChange = lazyWithRetry(() => import("./pages/ForcePasswordChange"));
const WinPage = lazyWithRetry(() => import("./pages/WinPage"));
const WinMailIn = lazyWithRetry(() => import("./pages/WinMailIn"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const ResponsibleGaming = lazyWithRetry(() => import("./pages/ResponsibleGaming"));
const SweepstakesRules = lazyWithRetry(() => import("./pages/SweepstakesRules"));
const LegalRestrictions = lazyWithRetry(() => import("./pages/LegalRestrictions"));
const HowItWorks = lazyWithRetry(() => import("./pages/HowItWorks"));
const RestrictedPage = lazyWithRetry(() => import("./pages/RestrictedPage"));
const RewardsPage = lazyWithRetry(() => import("./pages/RewardsPage"));
const GiveawaysPage = lazyWithRetry(() => import("./pages/GiveawaysPage"));
const ChallengesPage = lazyWithRetry(() => import("./pages/ChallengesPage"));
const GameProviders = lazyWithRetry(() => import("./pages/GameProviders"));
const About = lazyWithRetry(() => import("./pages/About"));
const SecurityPolicy = lazyWithRetry(() => import("./pages/SecurityPolicy"));
const AMOE = lazyWithRetry(() => import("./pages/AMOE"));
const ComplianceSummary = lazyWithRetry(() => import("./pages/ComplianceSummary"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const ContactUs = lazyWithRetry(() => import("./pages/ContactUs"));
const RefundPolicy = lazyWithRetry(() => import("./pages/RefundPolicy"));
const ShippingPolicy = lazyWithRetry(() => import("./pages/ShippingPolicy"));
const CertificateOfControl = lazyWithRetry(() => import("./pages/CertificateOfControl"));
const ComplianceProof = lazyWithRetry(() => import("./pages/ComplianceProof"));
const ComplianceDocs = lazyWithRetry(() => import("./pages/ComplianceDocs"));
const KYCFlowDemo = lazyWithRetry(() => import("./pages/KYCFlowDemo"));
import SignUp from "./pages/SignUp";
import Login from "./pages/Login";
const BannedPage = lazyWithRetry(() => import("./pages/BannedPage"));
const Gameplay = lazyWithRetry(() => import("./pages/Gameplay"));
const Stats = lazyWithRetry(() => import("./pages/Stats"));
const WalletPage = lazyWithRetry(() => import("./pages/WalletPage"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const ArcadeHub = lazyWithRetry(() => import("./pages/ArcadeHub"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const PayoutPolicy = lazyWithRetry(() => import("./pages/PayoutPolicy"));
import SupportChatWidget from "./components/SupportChatWidget";
import DailyLoginPopup from "./components/DailyLoginPopup";
import ReconnectBanner from "./components/ReconnectBanner";
import RoyalMarketingFrame from "./components/RoyalMarketingFrame";

export const API = `${window.location.origin}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Persist token across refreshes — only clear on browser/tab close
  const getInitialToken = () => {
    return sessionStorage.getItem("token");
  };

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getInitialToken);
  const [loading, setLoading] = useState(true);
  const [accountLocked, setAccountLocked] = useState(null); // { type, message }
  const [celebration, setCelebration] = useState(null); // { type: "level"|"tier", data }
  const [prevState, setPrevState] = useState({ level: null, vip_rank: null });
  // Reconnect state — shown during deploy rollover or any transient backend outage.
  // Hidden automatically once a successful response comes back.
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // ── 30-second user polling KILLED (Feb 2026 emergency) ─────────────
  // Was: `setInterval(fetchUser, 30000)` — every 30s poll of /auth/me was
  // the trigger that made the Level Up "Congratulations!" popup loop
  // nonstop on production. Explicitly disabled per ops directive.
  // If a specific screen needs fresh balances it must call fetchUser()
  // itself (game players + Store already do this).
  //

  // Auto-logout after 10 minutes of inactivity
  useEffect(() => {
    if (!token) return;
    const INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 minutes
    let timer = setTimeout(() => {
      sessionStorage.removeItem("token");
      setToken(null);
      setUser(null);
    }, INACTIVITY_LIMIT);

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        sessionStorage.removeItem("token");
        setToken(null);
        setUser(null);
      }, INACTIVITY_LIMIT);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [token]);

  // Axios interceptor:
  //   1. Catches ACCOUNT_LOCKED responses
  //   2. Shows "Servers are busy" banner on backend downtime (deploys, restarts, OOM cycles)
  //   3. Auto-retries idempotent failed requests with exponential backoff
  //   4. Dismisses the banner on next successful response
  useEffect(() => {
    // Track failures to avoid toast spam and flip the banner after 3 consecutive failures
    let consecutiveFailures = 0;
    let bannerTimer = null;

    const clearBannerImmediate = () => {
      consecutiveFailures = 0;
      setReconnecting(false);
      if (bannerTimer) { clearTimeout(bannerTimer); bannerTimer = null; }
    };

    const okInterceptor = axios.interceptors.response.use(
      (response) => {
        // Any successful response IMMEDIATELY clears the banner
        clearBannerImmediate();
        return response;
      },
      async (error) => {
        // Handle account-lock first
        const detail = error.response?.data?.detail || "";
        if (error.response?.status === 403 && typeof detail === "string" && detail.startsWith("ACCOUNT_LOCKED")) {
          const parts = detail.split("|");
          const lockType = parts[1] || "locked";
          const lockMsg = parts[2] || "Your account is locked.";
          // Banned users get a dedicated /banned surface with the specific
          // reason inline — much clearer than a generic modal.
          if (lockType === "banned") {
            try {
              const qs = `?reason=${encodeURIComponent(lockMsg)}`;
              if (window.location.pathname !== "/banned") {
                window.location.replace(`/banned${qs}`);
              }
            } catch (e) { void e; }
            return Promise.reject(error);
          }
          setAccountLocked({ type: lockType, message: lockMsg });
          return Promise.reject(error);
        }

        const status = error.response?.status;
        const isNetworkError = !error.response;
        const isTransient = isNetworkError || [502, 503, 504].includes(status);
        const cfg = error.config || {};

        // Retry idempotent requests (GET/HEAD) up to 3 times with exponential backoff.
        // Covers a ~7s deploy rollover or OOM-restart cycle invisibly.
        const method = (cfg.method || "get").toLowerCase();
        const isIdempotent = method === "get" || method === "head";
        cfg.__retryCount = cfg.__retryCount || 0;

        if (isTransient && isIdempotent && cfg.__retryCount < 3) {
          cfg.__retryCount += 1;
          consecutiveFailures += 1;
          // Show banner only after 3 consecutive failures — less alarmist
          if (consecutiveFailures >= 3) setReconnecting(true);
          // Backoff: 1s, 2s, 4s (sum ≈ 7s of coverage — enough for K8s pod restart)
          const delay = Math.min(4000, 1000 * Math.pow(2, cfg.__retryCount - 1));
          await new Promise((r) => setTimeout(r, delay));
          return axios(cfg);
        }

        // On transient, non-idempotent failures: just flip the banner, don't retry (unsafe).
        if (isTransient) {
          consecutiveFailures += 1;
          if (consecutiveFailures >= 3) setReconnecting(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(okInterceptor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newUser = response.data;

      // ── Level Up / Tier Upgrade celebration KILLED (Feb 2026 emergency) ──
      // The popup was looping nonstop in production because of stale-closure
      // reads + 30s polling. Both the polling and the popup render have been
      // physically removed. This detection block is gone with them.
      //
      setPrevState({ level: newUser.level || 1, vip_rank: newUser.vip_rank || "Bronze" });

      // One-shot SC backfill banner — if the user is owed level-up SC that we just
      // credited via /admin/reconcile/level-up-sc-by-current-level, surface a clear
      // toast so they don't think it's a glitch.
      try {
        const seenKey = `jr_lvlup_backfill_seen_${newUser.id || newUser.email || "anon"}`;
        if (!sessionStorage.getItem(seenKey)) {
          sessionStorage.setItem(seenKey, "1");
          const nRes = await axios.get(`${API}/notifications`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const backfill = (nRes.data?.notifications || []).find(
            (n) => !n.read && (n.id || "").includes("_lvlup_backfill_")
          );
          if (backfill) {
            // Fire a high-visibility toast + mark notification as read
            const { default: toast } = await import("react-hot-toast");
            toast.success(
              `${backfill.title}\n${backfill.message}`,
              { duration: 10000, position: "top-center",
                style: { background: "#0a0a0a", color: "#fff", border: "1px solid rgba(34,197,94,0.4)",
                         maxWidth: "500px", whiteSpace: "pre-line" } }
            );
            axios.post(`${API}/notifications/${backfill.id}/read`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            }).catch(() => {});
          }
        }
      } catch (_e) { /* notification banner is best-effort */ }

      // First-time initialization: pre-seed the celebrated tier AND level so
      // existing users (e.g. at Royal / Level 25) don't see the popup on
      // their next login as if they just upgraded.
      if (prevState.level === null && newUser.vip_rank) {
        const userId = newUser.id || newUser.email || "anon";
        const celebratedKey = `jr_celebrated_tier_${userId}`;
        if (!localStorage.getItem(celebratedKey)) {
          localStorage.setItem(celebratedKey, newUser.vip_rank);
        }
        const celebratedLevelKey = `jr_celebrated_level_${userId}`;
        const currentLevel = newUser.level || 1;
        const storedLevel = parseInt(localStorage.getItem(celebratedLevelKey) || "0", 10);
        if (currentLevel > storedLevel) {
          localStorage.setItem(celebratedLevelKey, String(currentLevel));
        }
      }
      // Only update user state if something meaningful actually changed — avoids
      // unnecessary re-renders across the whole app every 30 s.
      setUser((prev) => {
        if (!prev) return newUser;
        const fields = ["sweeps_coins", "gold_coins", "level", "vip_rank", "verification_status", "sc_bonus_balance", "sc_redeemable", "games_locked", "xp", "giveaway_entries"];
        const changed = fields.some(f => prev[f] !== newUser[f]);
        return changed ? newUser : prev;
      });

      // Ping the Redeemable pill (PlaythroughProgress widget) so it refreshes
      // the moment /auth/me returns fresh balances after a bet is settled.
      try { window.dispatchEvent(new Event("sc:bet-settled")); } catch (_) { /* no-op */ }
      
      // Check for account lock from /auth/me response
      if (newUser.lock_type && newUser.lock_until) {
        const lockUntil = new Date(newUser.lock_until);
        const msg = newUser.lock_type === "self_exclusion"
          ? `Your account is under self-exclusion until ${lockUntil.toLocaleString()}. Contact support to request early removal.`
          : `Your account is on a break until ${lockUntil.toLocaleString()}. Contact support to end your break early.`;
        setAccountLocked({ type: newUser.lock_type, message: msg });
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      // Only logout on 401 (invalid/expired token), not on network errors
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    sessionStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    const u = response.data.user;
    setUser(u);
    setPrevState({ level: u.level || 1, vip_rank: u.vip_rank || "Bronze" });
    return response.data;
  };

  const register = async (payload, password, username) => {
    // Accepts either (email, password, username) for legacy calls OR a full
    // payload object { email, password, username, first_name, ... }
    const body = typeof payload === "string"
      ? { email: payload, password, username }
      : payload;
    const response = await axios.post(`${API}/auth/register`, body);
    sessionStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    const u = response.data.user;
    setUser(u);
    setPrevState({ level: u.level || 1, vip_rank: u.vip_rank || "Bronze" });
    return response.data;
  };

  const logout = () => {
    sessionStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const updateBalance = (goldCoins, sweepsCoins) => {
    setUser((prev) => ({
      ...prev,
      gold_coins: goldCoins !== undefined ? goldCoins : prev.gold_coins,
      sweeps_coins: sweepsCoins !== undefined ? sweepsCoins : prev.sweeps_coins,
    }));
  };

  // ── Suppress the Level-Up modal on the PREVIEW environment only. ──
  // KA Gaming certification tests run on preview and their tooling doesn't
  // handle unexpected overlays. Production behaviour is unchanged.
  const isPreviewEnv = typeof window !== "undefined"
    && /preview\.emergentagent\.com$/i.test(window.location.hostname);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateBalance, fetchUser, refreshUser: fetchUser, accountLocked, setAccountLocked, celebration, setCelebration }}>
      {children}
      <ReconnectBanner active={reconnecting} />
      {/* LevelUpCelebration popup removed (Feb 2026 emergency) — was looping in prod. */}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading, accountLocked, setAccountLocked } = useAuth();
  const location = useLocation();
  const [locationVerified, setLocationVerified] = useState(() => sessionStorage.getItem("jr_location_verified") === "true");
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="animate-spin-slow text-4xl">&#x1F3AE;</div></div>;
  if (!user) return <Navigate to="/" />;

  // Admins and moderators bypass the location-verification gate entirely
  // (geo-block is for player compliance, not platform operators)
  const isAdminOrMod = user?.is_admin || user?.is_moderator || user?.role === "admin" || user?.role === "moderator";
  // Provider cert teams (Mascot/Booming/KA/etc.) need to access the lobby
  // from outside the US for testing — skip the GPS/IP gate for them.
  const isProviderTester = !!(user?.is_provider_tester || user?.is_ka_tester || user?.is_mascot_tester);
  const isAdminRoute = location.pathname.startsWith("/admin");
  const skipLocationCheck = isAdminOrMod || isAdminRoute || isProviderTester;

  // Show location verification modal if not verified this session (skip for admins / admin routes)
  if (!locationVerified && !skipLocationCheck) {
    return (
      <LocationVerifyModal
        onVerified={() => setLocationVerified(true)}
        onBlocked={() => navigate("/restricted?reason=gps")}
      />
    );
  }

  // Allow profile page even when locked (so user can manage RG settings or contact support)
  const isAllowedWhileLocked = location.pathname === "/profile" || location.pathname === "/support";

  if (accountLocked && !isAllowedWhileLocked) {
    return (
      <div className="min-h-screen bg-[#050814] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-[#1a2332] rounded-2xl border border-white/10 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#4d7cff]/10 flex items-center justify-center mx-auto mb-5">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#4d7cff]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2" data-testid="account-locked-title">
            {accountLocked.type === "self_exclusion" ? "Account Self-Excluded" : "Account on Break"}
          </h2>
          <p className="text-zinc-400 text-sm mb-6" data-testid="account-locked-message">{accountLocked.message}</p>
          <div className="space-y-3">
            <a href="/profile" className="block w-full py-3 bg-white/[0.05] border border-white/10 rounded-xl text-white text-sm font-semibold hover:bg-white/[0.08] transition-all" data-testid="locked-profile-btn">
              Manage Responsible Gaming Settings
            </a>
            <button onClick={() => setAccountLocked(null)} className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 rounded-xl text-white text-sm font-bold transition-all" data-testid="locked-dismiss-btn">
              Dismiss
            </button>
          </div>
          <p className="text-zinc-500 text-xs mt-4">Need help? Contact support or email jackroyalsllc@gmail.com</p>
        </div>
      </div>
    );
  }

  // Show DashboardNav on non-game pages — DISABLED, using sidebar nav
  return children;
};

function App() {
  // The first render that completes successfully clears the chunk-reload flag
  // so we'll automatically recover the *next* time a deploy lands.
  useEffect(() => { clearChunkReloadFlag(); }, []);
  return (
    <GeoProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App min-h-screen bg-[#050505]">
            <ChunkErrorBoundary>
            <Suspense fallback={
              <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#050505]">
                <div className="text-amber-400 text-sm font-bold flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
                  Loading…
                </div>
              </div>
            }>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payout-policy" element={<PayoutPolicy />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/my-referrals" element={<MyReferralsPage />} />
            <Route path="/my-referrals/:code" element={<MyReferralsPage />} />
            <Route path="/why-crypto" element={<WhyCrypto />} />
            <Route path="/tournaments" element={<TournamentsPage />} />
            <Route path="/force-password-change" element={<ForcePasswordChange />} />
            <Route path="/login" element={<Login />} />
            <Route path="/lobby" element={<ProtectedRoute><GameLobby /></ProtectedRoute>} />
            <Route path="/games" element={<ProtectedRoute><GameLobby /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/arcade" element={<ProtectedRoute><ArcadeHub /></ProtectedRoute>} />
            <Route path="/hub" element={<ProtectedRoute><ArcadeHub /></ProtectedRoute>} />
            <Route path="/game/slot/:gameId" element={<ProtectedRoute><PremiumSlotGame /></ProtectedRoute>} />
            <Route path="/game/multi/:gameId" element={<ProtectedRoute><MultiLayoutSlot /></ProtectedRoute>} />
            <Route path="/game/table/:gameId" element={<ProtectedRoute><Blackjack /></ProtectedRoute>} />
            <Route path="/game/blackjack" element={<ProtectedRoute><Blackjack /></ProtectedRoute>} />
            <Route path="/game/slotopol/:gameId" element={<ProtectedRoute><SlotopolGame /></ProtectedRoute>} />
            <Route path="/game/external/:gameId" element={<ProtectedRoute><ExternalSlotGame /></ProtectedRoute>} />
            <Route path="/game/booming/:gameId" element={<ProtectedRoute><BoomingGamePlayer /></ProtectedRoute>} />
            <Route path="/game/betsoft/:gameId" element={<ProtectedRoute><BetsoftGamePlayer /></ProtectedRoute>} />
            <Route path="/game/mascot/:gameId" element={<ProtectedRoute><MascotGamePlayer /></ProtectedRoute>} />
            <Route path="/game/ka/:gameId" element={<ProtectedRoute><KAGamePlayer /></ProtectedRoute>} />
            <Route path="/game/spinjoy/:gameCode" element={<ProtectedRoute><SpinjoyGamePlayer /></ProtectedRoute>} />
            <Route path="/game/petersons/:gameId" element={<ProtectedRoute><PetersonsGamePlayer /></ProtectedRoute>} />
            <Route path="/game/crash" element={<ProtectedRoute><CrashGame /></ProtectedRoute>} />
            <Route path="/game/roulette" element={<ProtectedRoute><RouletteGame /></ProtectedRoute>} />
            <Route path="/daily-wheel" element={<ProtectedRoute><DailyWheel /></ProtectedRoute>} />
            <Route path="/game/poker" element={<ProtectedRoute><PokerGame /></ProtectedRoute>} />
            <Route path="/jackpots" element={<JackpotPage />} />
            <Route path="/referral" element={<ProtectedRoute><ReferralProgram /></ProtectedRoute>} />
            <Route path="/verification" element={<ProtectedRoute><RedeemErrorBoundary><VerificationPage /></RedeemErrorBoundary></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/gameplay" element={<ProtectedRoute><Gameplay /></ProtectedRoute>} />
            <Route path="/stats" element={<ProtectedRoute><Stats /></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/store" element={<ProtectedRoute><Store /></ProtectedRoute>} />
            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/win" element={<WinPage />} />
            <Route path="/win/mail-in" element={<WinMailIn />} />
            <Route path="/redeem" element={<ProtectedRoute><RedeemErrorBoundary><RedeemPage /></RedeemErrorBoundary></ProtectedRoute>} />
            <Route path="/redeem/status" element={<ProtectedRoute><RedeemErrorBoundary><RedemptionStatusPage /></RedeemErrorBoundary></ProtectedRoute>} />
            <Route path="/moderator" element={<ProtectedRoute><ModeratorDashboard /></ProtectedRoute>} />
            <Route path="/senior-mod" element={<ProtectedRoute><SeniorModDashboard /></ProtectedRoute>} />
            <Route path="/tax/w9" element={<ProtectedRoute><W9FormPage /></ProtectedRoute>} />
            <Route path="/tax/documents" element={<ProtectedRoute><TaxDocumentsPage /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/giveaway-wheel/:giveawayId" element={<ProtectedRoute><GiveawayWheel /></ProtectedRoute>} />
            <Route path="/admin/providers" element={<ProtectedRoute><GameProviders /></ProtectedRoute>} />
            <Route path="/admin/games" element={<ProtectedRoute><AdminGameControls /></ProtectedRoute>} />
            <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
            <Route path="/giveaways" element={<ProtectedRoute><GiveawaysPage /></ProtectedRoute>} />
            <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/responsible-gaming" element={<ResponsibleGaming />} />
            <Route path="/sweepstakes-rules" element={<SweepstakesRules />} />
            <Route path="/legal-restrictions" element={<LegalRestrictions />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/about" element={<About />} />
            <Route path="/security" element={<SecurityPolicy />} />
            <Route path="/amoe" element={<AMOE />} />
            <Route path="/compliance" element={<ComplianceSummary />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/certificate-of-control" element={<CertificateOfControl />} />
            <Route path="/compliance-proof" element={<ComplianceProof />} />
            <Route path="/compliance-docs" element={<ComplianceDocs />} />
            <Route path="/kyc-flow" element={<KYCFlowDemo />} />
            <Route path="/restricted" element={<RestrictedPage />} />
            <Route path="/banned" element={<BannedPage />} />
          </Routes>
          </Suspense>
          </ChunkErrorBoundary>
          <SCBlockedBanner />
        </div>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1a1a1a',
              color: '#fff',
              border: '2px solid #F59E0B',
              padding: '16px',
              borderRadius: '8px',
              fontSize: '14px',
              zIndex: 9999,
            },
            success: {
              duration: 4000,
              style: {
                background: '#065f46',
                border: '2px solid #10b981',
              },
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              style: {
                background: '#7f1d1d',
                border: '2px solid #ef4444',
              },
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
          containerStyle={{
            zIndex: 99999,
          }}
        />
        <SonnerToaster
          position="top-right"
          richColors
          closeButton
          theme="dark"
          toastOptions={{ style: { zIndex: 99999 } }}
        />
        <AgeVerification />
        <ComplianceFilter />
        <GeoBlockCheck />
        <MobileNav />
        <ConditionalSupportChat />
        <RoyalMarketingFrameWrapper />
        <PersistentDailyPopup />
      </BrowserRouter>
    </AuthProvider>
  </GeoProvider>
  );
}

// Wrapper is defined below the AuthProvider so it can read the user via
// `useAuth`. Rendering inside <BrowserRouter> means useLocation() works.
function RoyalMarketingFrameWrapper() {
  const { user } = useAuth();
  return <RoyalMarketingFrame user={user} />;
}

// Hide the floating AI support widget on game pages so it doesn't cover game buttons.
// NOTE: DailyLoginPopup was previously nested here too, which caused it to
// unmount / remount every time the user entered or exited a game route — and
// that re-fired its mount effect, making the popup reappear repeatedly.
// The popup now lives at a stable top-level spot (see PersistentDailyPopup
// below) and only Support Chat is conditional here.
function ConditionalSupportChat() {
  const location = useLocation();
  const path = location.pathname;
  const hideOn = path.startsWith("/game/") || path === "/daily-wheel";
  if (hideOn) return null;
  return <SupportChatWidget />;
}

// Stable top-level mount for the daily login popup — never unmounts on
// route change, so its "shown today" gate + persistent localStorage marker
// keep it a strict once-per-day experience. Hard-blocked on `/game/*` and
// `/daily-wheel` so a full-screen overlay never covers a running game and
// traps the player until the 10-min inactivity auto-logout fires.
function PersistentDailyPopup() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  const path = location.pathname || "";
  if (path.startsWith("/game/") || path === "/daily-wheel") return null;
  return <DailyLoginPopup />;
}

// Component to check if user is geo-blocked
const GeoBlockCheck = () => {
  const geo = useGeo();
  const location = useLocation();
  
  if (geo?.loading) return null;
  if (geo?.isBlocked && location.pathname !== "/restricted") {
    const params = new URLSearchParams();
    if (geo?.userState) params.set("state", geo.userState);
    if (geo?.tier) params.set("tier", String(geo.tier));
    return <Navigate to={`/restricted?${params.toString()}`} replace />;
  }
  
  return null;
};

// Mobile Navigation for logged-in users — top bar + bottom bar.
// Hidden inside game canvases (/game/*, /booming/*) so the play area
// stays fullscreen. Visible everywhere else including the landing page.
const MobileNav = () => {
  const { user } = useAuth();
  const location = useLocation();

  const excludedPaths = ['/admin', '/game/', '/booming/', '/banned', '/restricted'];
  const shouldShow = user && !excludedPaths.some(path => location.pathname.startsWith(path));

  if (!shouldShow) return null;

  return <StickyBottomNav />;
};

export default App;
